import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { AppRegistryPage } from '@plato/app-meta/ai-meta';
import { usePageAiMeta } from '@plato/app-shared/hooks/use-page-ai-meta';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { useBreakpoint } from '@plato/ui/util/use-breakpoint';
import { formatISO, isWeekend, nextMonday, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { PageTitle } from '../../components/page-title.tsx';
import { EventsOverviewBodyDay } from '../../features/events-v2/components/events-overview-body-day.tsx';
import { EventsOverviewBodyWeek } from '../../features/events-v2/components/events-overview-body-week.tsx';
import { EventsOverviewHeaderNew } from '../../features/events-v2/components/events-overview-header-new.tsx';
import { EventsOverviewContentDay } from '../../features/events/components/events-overview-content-day.tsx';
import { EventsOverviewContentWeek } from '../../features/events/components/events-overview-content-week.tsx';
import { EventsOverviewHeader } from '../../features/events/components/events-overview-header.tsx';
import { useEventsListParams } from '../../features/events/hooks/use-events-list-params.ts';

export function useEventsAiMeta(): AppRegistryPage {
    const { t } = useTranslation();
    return usePageAiMeta({
        path: '/events',
        title: t('page_title__events', { ns: 'common' }),
        requires: ['enableCustomerEventPlanning'],
        description:
            'The visit calendar by day or week: planned customer visits and other appointments for ' +
            'the signed-in user or a subordinate, with suggested customers to visit next. Visits can ' +
            'be one-off or recurring; a recurring visit repeats weekly (every N weeks on chosen ' +
            'weekdays, optionally until an end date, with single dates skippable). Daily, monthly or ' +
            'yearly cadences are not supported. Send the user here to plan or look up visits, ' +
            'including setting up a recurring visit. Visits that already took place are logged as ' +
            'notes and show in the activity feeds, not here.',
    });
}

function EventsPage() {
    const { t } = useTranslation();
    const { enableNewEventPage } = useAppConfig();
    const {
        params,
        days,
        day,
        setDay,
        viewMode: savedViewMode,
        setViewMode,
        ownerId,
        setOwnerId,
    } = useEventsListParams();
    const isDesktop = useBreakpoint('sm');
    // The new page has no week view on phones; the saved choice still applies on larger screens
    const viewMode = enableNewEventPage && !isDesktop ? 'day' : savedViewMode;
    // The new day view never shows a weekend: a Saturday or Sunday, such as today, shows the Monday after it
    const shownDay =
        enableNewEventPage && viewMode === 'day' && isWeekend(parseISO(day))
            ? formatISO(nextMonday(parseISO(day)), { representation: 'date' })
            : day;

    return (
        <Page>
            <PageTitle title={t('page_title__events')} />
            {enableNewEventPage ? (
                <EventsOverviewHeaderNew
                    day={shownDay}
                    onChangedDay={setDay}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    selectedOwnerId={ownerId}
                    onOwnerChange={setOwnerId}
                />
            ) : (
                <EventsOverviewHeader
                    day={day}
                    onChangedDay={setDay}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    selectedOwnerId={ownerId}
                    onOwnerChange={setOwnerId}
                />
            )}

            {enableNewEventPage ? (
                <div
                    // Runs into the page's padding: the week grid scrolls sideways right to the window edge,
                    // and a swiped day slides out of view there. Each view pads back to the page's padding.
                    className="-mx-5 min-h-0 flex-1 overflow-auto sm:-mx-4"
                >
                    {viewMode === 'day' ? (
                        <EventsOverviewBodyDay day={shownDay} ownerId={ownerId} onChangedDay={setDay} />
                    ) : (
                        <EventsOverviewBodyWeek
                            dateFrom={params.dateFrom}
                            dateTo={params.dateTo}
                            ownerId={ownerId}
                            days={days}
                        />
                    )}
                </div>
            ) : (
                <>
                    <hr />
                    <div className="flex-1 overflow-y-auto">
                        {viewMode === 'day' ? (
                            <EventsOverviewContentDay day={day} ownerId={ownerId} />
                        ) : (
                            <EventsOverviewContentWeek
                                dateFrom={params.dateFrom}
                                dateTo={params.dateTo}
                                ownerId={ownerId}
                                days={days}
                            />
                        )}
                    </div>
                </>
            )}
        </Page>
    );
}

export default EventsPage;
